import {GraphQLErrorExtensions} from 'graphql';
import {ClientError, GraphQLClient, RequestDocument} from 'graphql-request';
import {useAccessRestrictionModal} from '@/components/AccessRestrictionModalSystem';
import {
  SessionErrorType,
  useSessionErrorModal,
} from '@/components/SessionErrorModalSystem';
import toast from '@/components/HotToast';
import {AccessRestrictionType} from '@/enums';
import {useQueryClient} from '@tanstack/react-query';
import {LOGOUT, ME_QUERY_KEY, REFRESH_TOKEN} from './session';

interface IRequestOptions {
  query: RequestDocument;
  variables?: any;
  requestHeaders?: HeadersInit;
}

interface ErrorExtensions extends GraphQLErrorExtensions {
  code: number;
  timestamp: number;
  extra: any;
  stacktrace: string[];
}

const TOO_MANY_REQUESTS_TOAST_ID = 'api-too-many-requests';
const TOO_MANY_REQUESTS_CODE = 429;
const TOO_MANY_REQUESTS_MESSAGE =
  '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.';

// 이용 제한 거부 응답 (extensions.code 400, extensions.extra.code)
const ACCESS_RESTRICTION_ERROR_CODES: {[code: string]: AccessRestrictionType} =
  {
    ACCOUNT_RESTRICTED: AccessRestrictionType.Account,
    IP_RESTRICTED: AccessRestrictionType.Ip,
  };

const gqlClient = new GraphQLClient(
  `${process.env.REACT_APP_BACKEND_ENDPOINT}/graphql`,
  {
    credentials: 'include',
  }
);

export const getGqlErrorCodes = (error: ClientError) => {
  if (!error?.response?.errors) {
    return [];
  }

  const codes = (error.response.errors?.map(
    (error) => error?.extensions?.code
  ) ?? []) as string[];

  return codes;
};

export const getGqlErrorDetailData = (error: ClientError | any) => {
  return (error as ClientError)?.response?.errors?.[0]
    ?.extensions as ErrorExtensions;
};

const isTooManyRequestsError = (error: ClientError) => {
  const responseStatus = error?.response?.status;
  if (responseStatus === TOO_MANY_REQUESTS_CODE) {
    return true;
  }

  const extensions = getGqlErrorDetailData(error);
  return extensions?.code === TOO_MANY_REQUESTS_CODE;
};

export const getAccessRestrictionType = (
  error: ClientError | any
): AccessRestrictionType | undefined => {
  const code = getGqlErrorDetailData(error)?.extra?.code;

  return typeof code === 'string'
    ? ACCESS_RESTRICTION_ERROR_CODES[code]
    : undefined;
};

/** 이용 제한 거부 응답 여부. 안내 모달은 공통 에러 핸들러가 띄우므로 호출부는 별도 에러 안내를 생략한다. */
export const isAccessRestrictionError = (error: ClientError | any) =>
  !!getAccessRestrictionType(error);

const DEFAULT_ERROR_MESSAGE =
  '일시적인 오류가 발생했습니다.<br />잠시 후 다시 시도해주세요.';

export const getGqlErrorMessage = (
  error: ClientError | any,
  defaultMessage: string | false = DEFAULT_ERROR_MESSAGE
) => {
  // 이용 제한 사유는 서버 문구를 노출하지 않고 안내 모달로만 알린다.
  if (isAccessRestrictionError(error)) {
    return defaultMessage || DEFAULT_ERROR_MESSAGE;
  }

  const message = (error as ClientError)?.response?.errors?.[0]
    ?.message as string;
  return /[ㄱ-힣]/.test(message) || !defaultMessage ? message : defaultMessage;
};

const useErrorHandler = () => {
  const queryClient = useQueryClient();

  const setSessionErrorModal = useSessionErrorModal();
  const openAccessRestrictionModal = useAccessRestrictionModal();

  const clearSession = () => {
    queryClient.setQueryData([ME_QUERY_KEY], () => ({}));
  };

  const logout = async () =>
    await gqlClient.request(LOGOUT).finally(() => clearSession());

  const errorHandler = async (error: ClientError) => {
    if (isTooManyRequestsError(error)) {
      toast.error(TOO_MANY_REQUESTS_MESSAGE, {
        id: TOO_MANY_REQUESTS_TOAST_ID,
      });
      throw error;
    }

    const restriction = getAccessRestrictionType(error);

    if (restriction) {
      openAccessRestrictionModal(restriction);
      throw error;
    }

    const extensions = getGqlErrorDetailData(error);

    if (extensions?.code === 401) {
      switch (extensions.extra?.code) {
        case -1: // 토큰 만료
          try {
            const data: any = await gqlClient.request(REFRESH_TOKEN);

            if (data?.refresh) {
              // 토큰 갱신 후 실패한 요청 재시도
              extensions.extra.retry = true;
              // eslint-disable-next-line no-throw-literal
              throw {
                response: {
                  errors: [extensions],
                },
              };
            } else {
              await logout();
            }
          } catch (e) {
            // 재시도 강제 exception 아닌 경우 에러처리
            if (!extensions.extra.retry) {
              const retryExtention = getGqlErrorDetailData(e);
              console.log(e, retryExtention);
              if (retryExtention?.code === 401) {
                clearSession();
              }
            }
          }
          break;
        case -2: // 유저 정보 없음
        case -3: // 토큰정보 변경 (패스워드 변경, 서버에서 강제 세션 종료)
        default: // 인증 정보 없음 (extra.code 미포함, 토큰 완전 만료 등)
          if (extensions.extra?.code === -3) {
            // 동시로그인 허용을 하지 않기 때문에 패스워드 변경 에러 이전에 중복로그인 에러가 먼저 발생할 것이므로 무조건 동시로그인 에러로 처리함.
            setSessionErrorModal(SessionErrorType.duplicated);
          }
          clearSession();
          break;
      }
    }

    throw error;
  };

  return errorHandler;
};

export const useGqlRequest = ({
  query,
  variables,
  requestHeaders,
}: IRequestOptions) => {
  const errorHandler = useErrorHandler();
  const request = async (reqVariables?: IRequestOptions['variables']) => {
    const omitTypename = (key: any, value: any) =>
      key === '__typename' ? undefined : value;

    const vars = JSON.parse(
      JSON.stringify(reqVariables ?? variables),
      omitTypename
    );

    const data: any = await gqlClient
      .request(query, vars, requestHeaders)
      .catch(errorHandler);

    if (data) {
      data.variables = vars;
    }

    return data;
  };

  return request;
};
